import {useState, useEffect} from 'react';
import {Spinner} from 'react-bootstrap';
import dayjs from 'dayjs';
import {Link, useNavigate} from 'react-router';
import DateRangePicker from '../components/DateRangePicker';

import ConfirmModal from '../components/ConfirmModal';
import * as OralPhotoApi from '../api/OralPhotoApi';
import PageShell from '../components/PageShell';
import ModelViewer from '../components/ModelViewer';
import oralPhotoImage from '../assets/image/icon-photos.png';
import './OralPhotos.css';

const TOKENS = {
  primary: '#C43B71',
  blush: '#FFF0F5',
  surfaceLow: '#F9FAFB',
  ghostBorder: 'rgba(196, 59, 113, 0.16)',
  ink: '#1A1A2E',
  inkMuted: '#6B7280',
  shadowSoft: '0 4px 12px rgba(196, 59, 113, 0.08)',
};

const FONT = {
  body: 'var(--font-body), Manrope, system-ui',
};

function fmtFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function fmtDate(isoStr) {
  if (!isoStr) return '';
  return dayjs(isoStr).format('D MMM YYYY');
}

/* --- Reusable UI Components --- */
function SegmentedControl({options, value, onChange}) {
  return (
    <div
      role="radiogroup"
      style={{
        display: 'inline-flex',
        padding: 4,
        borderRadius: 999,
        gap: 2,
        background: 'rgba(196, 59, 113, 0.08)',
      }}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(o.value)}
            className="ft-segment"
            style={{
              minWidth: 0,
              height: 44,
              padding: '0 14px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: checked ? 'rgba(255,255,255,0.94)' : 'transparent',
              boxShadow: checked ? TOKENS.shadowSoft : 'none',
              color: checked ? TOKENS.primary : TOKENS.ink,
              fontFamily: FONT.body,
              fontSize: options.length > 2 ? 13.5 : 14,
              fontWeight: checked ? 700 : 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ImageCard({image, expanded, onToggleExpand, onDelete, onEdit}) {
  return (
    <div
      style={{
        padding: 0,
        overflow: 'hidden',
        background: 'white',
        borderRadius: '20px',
        border: `1px solid ${TOKENS.ghostBorder}`,
        boxShadow: TOKENS.shadowSoft,
        marginBottom: 10,
      }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggleExpand}
        style={{
          width: '100%',
          minHeight: 84,
          padding: '12px 16px',
          border: 'none',
          background: expanded ? 'rgba(196, 59, 113, 0.02)' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textAlign: 'left',
          transition: 'background 160ms ease',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 160ms ease',
            flexShrink: 0,
          }}
        >
          <path
            d="M9 6l6 6-6 6"
            stroke={TOKENS.primary}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            flexShrink: 0,
            overflow: 'hidden',
            background: image.type === '3D' ? 'rgba(196, 59, 113, 0.08)' : TOKENS.surfaceLow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {image.type === '3D' ? (
            <ModelViewer
              url={image.url}
              fileType={image.url.toLowerCase().includes('.ply') ? 'ply' : 'stl'}
              isThumbnail={true}
              style={{width: '100%', height: '100%', background: 'transparent', borderRadius: 0}}
            />
          ) : (
            <img
              src={image.url}
              alt=""
              style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
            />
          )}
        </div>

        <div style={{flex: 1, minWidth: 0}}>
          <p
            style={{
              fontFamily: FONT.body,
              fontSize: 15,
              fontWeight: 600,
              color: TOKENS.ink,
              margin: '0 0 6px',
            }}
          >
            {fmtDate(image.shotDate)}
          </p>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            <span
              style={{
                background: 'rgba(26, 26, 46, 0.06)',
                color: TOKENS.ink,
                padding: '4px 8px',
                borderRadius: 6,
                fontFamily: FONT.body,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {image.type}
            </span>
            {image.type === '3D' && (
              <span style={{fontFamily: FONT.body, fontSize: 13, color: TOKENS.inkMuted}}>
                {fmtFileSize(image.sizeBytes)}
              </span>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{borderTop: `1px solid ${TOKENS.ghostBorder}`}}>
          {image.type !== '3D' && (
            <div style={{padding: '12px 16px', borderBottom: `1px solid ${TOKENS.ghostBorder}`}}>
              <p style={{fontFamily: FONT.body, fontSize: 13, color: TOKENS.inkMuted, margin: 0}}>
                {fmtDate(image.shotDate)}
              </p>
            </div>
          )}

          {image.type === '3D' ? (
            <div
              style={{
                background: TOKENS.blush,
                borderBottom: `1px solid ${TOKENS.ghostBorder}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ModelViewer
                url={image.url}
                fileType={image.url.toLowerCase().includes('.ply') ? 'ply' : 'stl'}
              />
              <div style={{textAlign: 'center', padding: 16}}>
                <p
                  style={{
                    fontFamily: FONT.body,
                    fontSize: 14,
                    fontWeight: 600,
                    color: TOKENS.ink,
                    margin: '0 0 4px',
                  }}
                >
                  {image.fileName || '3D Scan'}
                </p>
                <p style={{fontFamily: FONT.body, fontSize: 13, color: TOKENS.inkMuted, margin: 0}}>
                  {fmtFileSize(image.sizeBytes)} • {fmtDate(image.shotDate)}
                </p>
              </div>
            </div>
          ) : (
            <div
              style={{padding: 16, background: TOKENS.blush, borderBottom: `1px solid ${TOKENS.ghostBorder}`}}
            >
              <img
                src={image.url}
                alt=""
                style={{width: '100%', height: 'auto', borderRadius: 12, display: 'block'}}
              />
            </div>
          )}

          {image.note && (
            <div style={{padding: 16, borderBottom: `1px solid ${TOKENS.ghostBorder}`}}>
              <p
                style={{
                  fontFamily: FONT.body,
                  fontSize: 12,
                  fontWeight: 700,
                  color: TOKENS.inkMuted,
                  margin: '0 0 8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                Note
              </p>
              <p
                style={{
                  fontFamily: FONT.body,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: TOKENS.ink,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {image.note}
              </p>
            </div>
          )}

          <div style={{display: 'flex', gap: 8, padding: '12px 16px'}}>
            <a
              href={image.url}
              download
              title="Download"
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: TOKENS.primary,
                transition: 'background 160ms ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(196, 59, 113, 0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 16V5M7 10l5 5 5-5M4 17v1.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <button
              type="button"
              title="Edit"
              onClick={onEdit}
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: TOKENS.primary,
                transition: 'background 160ms ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(196, 59, 113, 0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 21l.5-5.5L17 3z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              title="Delete"
              onClick={onDelete}
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: TOKENS.primary,
                transition: 'background 160ms ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(196, 59, 113, 0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 6H5v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6m-2 0V4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2M10 11v6M14 11v6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Main Route --- */
export default function OralPhotos() {
  const navigate = useNavigate();
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  const [radioValue, setRadioValue] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [openedOralPhoto, setOpenedOralPhoto] = useState(null);

  const [oralPhotos, setOralPhotos] = useState([]);
  const [retrieveOralPhotos, setRetrieveOralPhotos] = useState(false);

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').toDate(),
    dayjs().add(1, 'month').toDate(),
  ]);

  const handleChangeDateRange = (date) => {
    if (date) {
      setDateRange([date[0], date[1]]);
    } else {
      setDateRange(null);
    }
  };

  useEffect(() => {
    setRetrieveOralPhotos(false);

    let apiRange = ['', ''];
    if (dateRange) {
      apiRange = [dayjs(dateRange[0]).format('YYYY-MM-DD'), dayjs(dateRange[1]).format('YYYY-MM-DD')];
    }

    OralPhotoApi.getAllOralPhotos(radioValue, apiRange)
      .then((photos) => {
        setRetrieveOralPhotos(true);
        if (Array.isArray(photos)) {
          setOralPhotos(photos);
        } else {
          console.error('API returned non-array:', photos);
          setOralPhotos([]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch oral photos:', err);
        setRetrieveOralPhotos(true);
        setOralPhotos([]);
      });
  }, [radioValue, dateRange]);

  const deleteOralPhoto = () => {
    if (!openedOralPhoto) return;
    OralPhotoApi.deleteOralPhoto(openedOralPhoto).then(() => {
      setOralPhotos((prev) => prev.filter((p) => p.id !== openedOralPhoto));
      setConfirmModalShow(false);
      setOpenedOralPhoto(null);
      setExpandedId(null);
    });
  };

  const hasFilters = radioValue !== 'all' || dateRange !== null;
  const showFilteredEmpty = hasFilters && oralPhotos.length === 0 && retrieveOralPhotos;
  const showFirstRunEmpty = !hasFilters && oralPhotos.length === 0 && retrieveOralPhotos;

  return (
    <PageShell title="Image/3D Storage" titleIcon={oralPhotoImage} showBackButton={true}>
      <div className="oral-photos-page">
        <div className="oral-photos-filter-bar">
          <div style={{flex: 1, minWidth: 200, display: 'flex'}}>
            <DateRangePicker
              onChange={handleChangeDateRange}
              value={dateRange}
              clearIcon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              }
              calendarIcon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3.5"
                    y="5"
                    width="17"
                    height="15"
                    rx="3"
                    stroke={TOKENS.primary}
                    strokeWidth="1.8"
                  />
                  <path
                    d="M3.5 10h17M8 3v4M16 3v4"
                    stroke={TOKENS.primary}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              }
            />
          </div>
          <SegmentedControl
            options={[
              {value: 'all', label: 'All'},
              {value: 'Photo', label: 'Photos'},
              {value: 'X-ray', label: 'X-rays'},
              {value: '3D', label: '3D'},
            ]}
            value={radioValue}
            onChange={setRadioValue}
          />
          <Link to="./add" className="oral-photos-add-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add new
          </Link>
        </div>

        {!retrieveOralPhotos && (
          <div style={{textAlign: 'center', padding: 40}}>
            <Spinner animation="border" style={{color: TOKENS.primary}} />
          </div>
        )}

        {showFirstRunEmpty && (
          <div className="oral-photos-empty">
            <div style={{marginBottom: 16}}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{margin: '0 auto'}}>
                <path
                  d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"
                  fill={TOKENS.primary}
                />
                <path
                  d="M8.5 11c.828 0 1.5-.672 1.5-1.5S9.328 8 8.5 8 7 8.672 7 9.5 7.672 11 8.5 11zm8 4.5L11 10l-5 6.5z"
                  fill={TOKENS.primary}
                />
              </svg>
            </div>
            <h3
              style={{
                fontFamily: FONT.body,
                fontSize: 18,
                fontWeight: 700,
                color: TOKENS.ink,
                margin: '0 0 8px',
              }}
            >
              Nothing stored yet
            </h3>
            <p style={{fontFamily: FONT.body, fontSize: 15, color: TOKENS.inkMuted, margin: '0 0 24px'}}>
              Add your first photo, X-ray, or 3D scan and it stays here for good.
            </p>
            <Link to="./add" className="oral-photos-add-btn">
              Add new
            </Link>
          </div>
        )}

        {showFilteredEmpty && (
          <div className="oral-photos-empty">
            <h3
              style={{
                fontFamily: FONT.body,
                fontSize: 18,
                fontWeight: 700,
                color: TOKENS.ink,
                margin: '0 0 8px',
              }}
            >
              No images found
            </h3>
            <p style={{fontFamily: FONT.body, fontSize: 15, color: TOKENS.inkMuted, margin: '0 0 24px'}}>
              Try adjusting your filters or date range to see more results.
            </p>
            <button
              className="oral-photos-add-btn"
              style={{background: 'white', color: TOKENS.primary, border: `1px solid ${TOKENS.primary}`}}
              onClick={() => {
                setRadioValue('all');
                setDateRange(null);
              }}
            >
              Clear filters
            </button>
          </div>
        )}

        {!showFirstRunEmpty && !showFilteredEmpty && oralPhotos.length > 0 && (
          <div>
            {oralPhotos.map((p) => {
              // Map backend structure to the POC Image structure
              const imageObj = {
                id: p.id,
                type: p.photo_type,
                shotDate: p.shoot_date || p.date,
                url: p.image_link,
                note: p.note,
                fileName: p.file_name || (p.photo_type === '3D' ? '3D Scan' : ''),
                sizeBytes: 0, // Backend size logic omitted for mock
              };
              return (
                <ImageCard
                  key={imageObj.id}
                  image={imageObj}
                  expanded={expandedId === imageObj.id}
                  onToggleExpand={() => setExpandedId(expandedId === imageObj.id ? null : imageObj.id)}
                  onDelete={() => {
                    setOpenedOralPhoto(imageObj.id);
                    setConfirmModalShow(true);
                  }}
                  onEdit={() => navigate(`./${imageObj.id}/edit`)}
                />
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        modalTitle="Delete Item"
        btnText="Delete"
        show={confirmModalShow}
        onHide={() => {
          setConfirmModalShow(false);
          setOpenedOralPhoto(null);
        }}
        clickBtn={deleteOralPhoto}
      >
        <p style={{fontSize: '1.2rem'}}>
          It will be removed from your storage. Your dentist keeps their own copy.
        </p>
      </ConfirmModal>
    </PageShell>
  );
}
